import { useMemo } from "react";
import {
    MAX_FLOW_FIELD_DOTS,
    buildFlowFieldDotSamples,
    getEffectiveDotMinGap,
} from "../../utils/flowFieldDots";
import { useSvgViewBoxZoom } from "../../hooks/useSvgViewBoxZoom";
import { FlowDebugSegmentLines } from "./FlowDebugSegmentLines";
import { useFlowField } from "./FlowFieldContext";

export function FlowFieldViewer() {
    const {
        viewBoxAttr,
        width,
        height,
        pathDs,
        curvePoints,
        drawDots,
        dotMinGapExtra,
        dotMinGapAtLeastStroke,
        debugSegments,
        strokeColor,
        strokeWidth,
        backgroundColor,
        showAngleDebug,
        canExport,
        handleDownload,
    } = useFlowField();

    const base = useMemo(() => {
        const [x, y, w, h] = viewBoxAttr.split(" ").map(Number);
        return { x, y, w, h };
    }, [viewBoxAttr]);

    const {
        svgRef,
        viewBoxAttr: viewBox,
        panHandlers,
        zoomPercent,
        zoomIn,
        zoomOut,
        resetView,
    } = useSvgViewBoxZoom({ base });

    const dotRadius = Math.max(0.08, strokeWidth / 2);
    const effectiveDotMinGap = getEffectiveDotMinGap(
        strokeWidth,
        dotMinGapExtra,
        dotMinGapAtLeastStroke,
    );
    const dotSampleInfo = useMemo(() => {
        if (!drawDots) return null;
        return buildFlowFieldDotSamples(
            curvePoints,
            MAX_FLOW_FIELD_DOTS,
            effectiveDotMinGap,
        );
    }, [curvePoints, drawDots, effectiveDotMinGap]);

    const dotsMeta = useMemo(() => {
        if (!drawDots || !dotSampleInfo) return null;
        const s = dotSampleInfo;
        const shown = s.samples.length;
        if (s.capped) {
            return `${shown.toLocaleString()} of ${s.afterMinGap.toLocaleString()} dots (capped at ${MAX_FLOW_FIELD_DOTS.toLocaleString()}; ${s.totalRaw.toLocaleString()} raw samples)`;
        }
        if (s.afterMinGap < s.totalRaw) {
            return `${shown.toLocaleString()} dot${shown !== 1 ? "s" : ""} (${s.totalRaw.toLocaleString()} raw samples)`;
        }
        return `${shown.toLocaleString()} dot${shown !== 1 ? "s" : ""}`;
    }, [drawDots, dotSampleInfo]);

    return (
        <div className="panel-viewer blob-field-viewer flow-field-viewer">
            <div className="path-tool-preview">
                <div className="svg-zoom-controls">
                    <button
                        type="button"
                        className="zoom-button"
                        onClick={zoomOut}
                        title="Zoom out"
                    >
                        −
                    </button>
                    <span className="zoom-level">{zoomPercent}%</span>
                    <button
                        type="button"
                        className="zoom-button"
                        onClick={zoomIn}
                        title="Zoom in"
                    >
                        +
                    </button>
                    <button
                        type="button"
                        className="zoom-button"
                        onClick={resetView}
                        title="Reset zoom"
                    >
                        ⌂
                    </button>
                </div>

                <div className="svg-scroll-wrapper">
                    <div className="svg-container svg-container--fill">
                        <svg
                            ref={svgRef}
                            viewBox={viewBox}
                            xmlns="http://www.w3.org/2000/svg"
                            className="path-tool-svg"
                            style={{ touchAction: "none" }}
                            {...panHandlers}
                        >
                            <rect
                                width={width}
                                height={height}
                                fill={backgroundColor}
                            />
                            <g className="flow-curves">
                                {drawDots
                                    ? (dotSampleInfo?.samples ?? []).map(
                                          (s) => (
                                              <circle
                                                  key={s.reactKey}
                                                  cx={s.x}
                                                  cy={s.y}
                                                  r={dotRadius}
                                                  fill={strokeColor}
                                              />
                                          ),
                                      )
                                    : pathDs.map((d, i) => (
                                          <path
                                              key={i}
                                              d={d}
                                              fill="none"
                                              stroke={strokeColor}
                                              strokeWidth={strokeWidth}
                                              strokeLinecap="round"
                                          />
                                      ))}
                            </g>
                            {showAngleDebug && debugSegments.length > 0 && (
                                <g className="flow-debug" opacity={0.55}>
                                    <FlowDebugSegmentLines
                                        segments={debugSegments}
                                        strokeWidth={0.2}
                                        gradientIdPrefix="ff-flow-dbg"
                                    />
                                </g>
                            )}
                        </svg>
                    </div>
                </div>

                <div className="path-tool-footer">
                    <p className="path-tool-meta">
                        {dotsMeta ??
                            `${pathDs.length} path${pathDs.length !== 1 ? "s" : ""}`}{" "}
                        - viewBox {viewBoxAttr}
                    </p>
                    <button
                        type="button"
                        className="generic-button"
                        disabled={!canExport}
                        onClick={handleDownload}
                    >
                        Download result SVG
                    </button>
                </div>
            </div>
        </div>
    );
}
